// P-TIER3-INSTRUMENTATION-A — ambient session metadata.
//
// The `sessions` aggregate row (migration 0000 + 0008) carries a few
// AMBIENT context fields the parent dashboard needs for auditability
// ("which app version / device / locale produced this session?"). They
// are ambient because they describe the runtime, NOT the in-session
// interaction — so they can be read from navigator + build env at write
// time WITHOUT any *Session.tsx edit. saveSession() (supabaseClient.js)
// stamps them on every row; both the legacy JSX path and the new TS
// finalizeSession path funnel through that one helper.
//
// The screen-coupled effort fields (activeMs, idleMs, pauseMs,
// appBackgroundedCount, completionState transitions) are NOT here — they
// need in-screen instrumentation and ship in E1b (task #49).
//
// The functions that DERIVE labels from raw inputs are pure (take the UA
// string / env as args) so they unit-test without a DOM. The runtime
// reader (ambientSessionMetadata) is the thin window/import.meta.env
// wrapper around them.

import { CURRICULUM_VERSION } from "./contentMetadata";

interface AmbientSessionMetadata {
  /** Build-injected app version (import.meta.env.VITE_APP_VERSION).
   *  null until CI sets the env — labeled honestly, never fabricated. */
  appVersion: string | null;
  /** Engine curriculum/content version (CURRICULUM_VERSION constant). */
  contentVersion: string;
  /** BCP-47 locale (navigator.language, e.g. "en-US"). null if unreadable. */
  locale: string | null;
  /** Coarse OS family parsed from the UA. null if unreadable. */
  os: string | null;
  /** Coarse device class parsed from the UA. null if unreadable. */
  deviceType: string | null;
  /** navigator.onLine negated — true when the runtime reports offline. */
  offlineFlag: boolean;
}

/** Coarse OS family from a User-Agent string. Pure. Returns null for a
 *  null/empty UA (so the column stays NULL rather than guessing). The
 *  order matters: iOS/iPadOS UAs also contain "Mac" tokens, so the
 *  Apple-mobile check must precede the macOS check. */
export function deriveOsFromUserAgent(ua: string | null | undefined): string | null {
  if (!ua) return null;
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";
  if (/Mac OS X|Macintosh/i.test(ua)) return "macOS";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Linux|X11|CrOS/i.test(ua)) return "Linux";
  return "other";
}

/** Coarse device class from a User-Agent string. Pure. "tablet" must be
 *  checked before "mobile" (iPad/Android-tablet UAs contain neither a
 *  phone token nor "Mobile" on iPadOS, but Android tablets omit the
 *  "Mobile" token that Android phones carry — that absence is the tablet
 *  signal). Returns null for a null/empty UA. */
export function deriveDeviceTypeFromUserAgent(ua: string | null | undefined): string | null {
  if (!ua) return null;
  if (/iPad/i.test(ua)) return "tablet";
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? "mobile" : "tablet";
  if (/iPhone|iPod/i.test(ua)) return "mobile";
  if (/Mobile/i.test(ua)) return "mobile";
  return "desktop";
}

/** Inputs the pure builder needs. Injected so tests don't touch a DOM. */
interface AmbientInputs {
  userAgent: string | null | undefined;
  language: string | null | undefined;
  online: boolean | null | undefined;
  appVersion: string | null | undefined;
}

/** Pure builder — maps raw runtime inputs to the ambient metadata shape.
 *  `online` defaults to true (assume online) when the runtime can't
 *  report it, so offlineFlag is false rather than a misleading true. */
export function buildAmbientSessionMetadata(inputs: AmbientInputs): AmbientSessionMetadata {
  return {
    appVersion: inputs.appVersion ?? null,
    contentVersion: CURRICULUM_VERSION,
    locale: inputs.language ?? null,
    os: deriveOsFromUserAgent(inputs.userAgent),
    deviceType: deriveDeviceTypeFromUserAgent(inputs.userAgent),
    offlineFlag: inputs.online === false,
  };
}

/** Runtime reader — pulls inputs from window.navigator + import.meta.env
 *  and delegates to the pure builder. SSR/no-DOM-safe (every field falls
 *  back to null/true). Call this at the saveSession() write site. */
export function ambientSessionMetadata(): AmbientSessionMetadata {
  const nav: Navigator | undefined =
    typeof navigator === "undefined" ? undefined : navigator;
  // import.meta.env is Vite-injected; guarded so a non-Vite test runtime
  // (raw tsx) doesn't throw on the access.
  let appVersion: string | null = null;
  try {
    appVersion =
      (import.meta as unknown as { env?: Record<string, string | undefined> })
        ?.env?.VITE_APP_VERSION ?? null;
  } catch {
    appVersion = null;
  }
  return buildAmbientSessionMetadata({
    userAgent: nav?.userAgent ?? null,
    language: nav?.language ?? null,
    online: nav?.onLine ?? true,
    appVersion,
  });
}
